import React, { useState } from 'react'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import { 
  Bell, 
  BellOff, 
  Mail, 
  Linkedin, 
  BarChart3, 
  TrendingUp, 
  Calendar,
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  MessageSquare,
  XCircle
} from 'lucide-react'
import { formatDistanceToNow } from 'date-fns'
import type { ClientNotificationSetting } from '@/hooks/useClientNotificationSettings'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'

interface ClientNotificationCardProps {
  setting: ClientNotificationSetting
  onUpdate: (clientId: string, updates: Record<string, boolean>) => Promise<boolean>
}

export function ClientNotificationCard({ setting, onUpdate }: ClientNotificationCardProps) {
  const [updating, setUpdating] = useState<string | null>(null)
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set(['email', 'linkedin', 'performance', 'outbound']))

  const toggleGroup = (group: string) => {
    setExpandedGroups(prev => {
      const next = new Set(prev)
      if (next.has(group)) {
        next.delete(group)
      } else {
        next.add(group)
      }
      return next
    })
  }

  const handleParentToggle = async (parentKey: string, value: boolean) => {
    setUpdating(parentKey)
    await onUpdate(setting.client_id, { [parentKey]: value })
    setUpdating(null)
  }

  const handleChildToggle = async (childKey: string, value: boolean) => {
    setUpdating(childKey)
    
    // Build update object with the child toggle
    const updates: Record<string, boolean> = { [childKey]: value }
    
    // Sync parent toggle based on all children (accounting for the new value)
    if (childKey.startsWith('email_') && !childKey.includes('replies_enabled')) {
      const allEnabled = (childKey === 'email_positive_first_enabled' ? value : setting.email_positive_first_enabled) && 
                        (childKey === 'email_positive_second_enabled' ? value : setting.email_positive_second_enabled) && 
                        (childKey === 'email_followup_enabled' ? value : setting.email_followup_enabled) && 
                        (childKey === 'email_negative_enabled' ? value : setting.email_negative_enabled)
      updates.email_replies_enabled = allEnabled
    }
    
    if (childKey.startsWith('linkedin_') && !childKey.includes('replies_enabled')) {
      const allEnabled = (childKey === 'linkedin_positive_first_enabled' ? value : setting.linkedin_positive_first_enabled) && 
                        (childKey === 'linkedin_positive_second_enabled' ? value : setting.linkedin_positive_second_enabled) && 
                        (childKey === 'linkedin_followup_enabled' ? value : setting.linkedin_followup_enabled) && 
                        (childKey === 'linkedin_negative_enabled' ? value : setting.linkedin_negative_enabled) && 
                        (childKey === 'linkedin_neutral_enabled' ? value : setting.linkedin_neutral_enabled)
      updates.linkedin_replies_enabled = allEnabled
    }
    
    if (childKey.startsWith('performance_') && !childKey.includes('reports_enabled')) {
      const allEnabled = (childKey === 'weekly_performance_enabled' ? value : setting.weekly_performance_enabled) && 
                        (childKey === 'monthly_performance_enabled' ? value : setting.monthly_performance_enabled)
      updates.performance_reports_enabled = allEnabled
    }
    
    if (childKey.startsWith('outbound_') && !childKey.includes('reports_enabled')) {
      const allEnabled = (childKey === 'daily_outbound_enabled' ? value : setting.daily_outbound_enabled) && 
                        (childKey === 'weekly_outbound_enabled' ? value : setting.weekly_outbound_enabled) && 
                        (childKey === 'monthly_outbound_enabled' ? value : setting.monthly_outbound_enabled)
      updates.outbound_reports_enabled = allEnabled
    }
    
    await onUpdate(setting.client_id, updates)
    setUpdating(null)
  }

  const isEnabled = setting.email_replies_enabled || 
                    setting.linkedin_replies_enabled ||
                    setting.performance_reports_enabled ||
                    setting.outbound_reports_enabled ||
                    setting.meeting_booked_enabled

  const hasChannels = setting.slack_positive_replies_channel || 
                      setting.slack_neutral_negative_replies_channel ||
                      setting.slack_main_channel ||
                      setting.slack_reporting_channel ||
                      setting.slack_sales_channel

  // Get statistics for display
  const getStatCount = (key: string): number => {
    return setting.notification_stats[key] || 0
  }

  const formatStat = (key: string): string => {
    const count = getStatCount(key)
    return count > 0 ? count.toString() : '0'
  }

  return (
    <Card className={`${isEnabled ? 'border-green-500/50 bg-green-500/5' : 'border-border'}`}>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${isEnabled ? 'bg-green-500/10' : 'bg-muted'}`}>
              {isEnabled ? (
                <Bell className="h-5 w-5 text-green-500" />
              ) : (
                <BellOff className="h-5 w-5 text-muted-foreground" />
              )}
            </div>
            <div>
              <h3 className="text-lg font-semibold">{setting.client_name}</h3>
              <div className="flex items-center gap-2 mt-1">
                <Badge variant="outline" className="text-xs">
                  {setting.client_status}
                </Badge>
                {setting.uses_linkedin && (
                  <Badge variant="secondary" className="text-xs">
                    LinkedIn
                  </Badge>
                )}
                {isEnabled ? (
                  <Badge className="bg-green-500/10 text-green-500 border-green-500/20">
                    Active
                  </Badge>
                ) : (
                  <Badge variant="secondary">
                    Inactive
                  </Badge>
                )}
              </div>
            </div>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Slack Channels Info */}
        {hasChannels && (
          <div className="space-y-2">
            <h4 className="text-sm font-medium text-muted-foreground">Slack Channels:</h4>
            <div className="grid gap-2 text-sm">
              {setting.slack_positive_replies_channel && (
                <div className="flex items-center gap-2 p-2 bg-muted/50 rounded-md">
                  <MessageSquare className="h-4 w-4 text-green-500" />
                  <span className="text-xs">Positive: <code className="bg-background px-1 rounded">{setting.slack_positive_replies_channel}</code></span>
                </div>
              )}
              {setting.slack_neutral_negative_replies_channel && (
                <div className="flex items-center gap-2 p-2 bg-muted/50 rounded-md">
                  <MessageSquare className="h-4 w-4 text-orange-500" />
                  <span className="text-xs">Negative/Neutral: <code className="bg-background px-1 rounded">{setting.slack_neutral_negative_replies_channel}</code></span>
                </div>
              )}
              {setting.slack_reporting_channel && (
                <div className="flex items-center gap-2 p-2 bg-muted/50 rounded-md">
                  <BarChart3 className="h-4 w-4 text-purple-500" />
                  <span className="text-xs">Reporting: <code className="bg-background px-1 rounded">{setting.slack_reporting_channel}</code></span>
                </div>
              )}
            </div>
          </div>
        )}

        {!hasChannels && (
          <div className="flex items-start gap-2 p-3 bg-amber-500/10 border border-amber-500/20 rounded-md">
            <AlertTriangle className="h-4 w-4 text-amber-500 mt-0.5" />
            <div className="text-sm text-amber-600 dark:text-amber-500">
              <p className="font-medium">No Slack channels configured</p>
              <p className="text-xs mt-1 text-muted-foreground">
                Configure channels in Client Settings before enabling notifications
              </p>
            </div>
          </div>
        )}

        {/* Notification Groups */}
        <div className="space-y-2 pt-3 border-t">
          <h4 className="text-sm font-medium text-muted-foreground">Notification Settings:</h4>
          
          {/* Email Reply Notifications */}
          <Collapsible open={expandedGroups.has('email')} onOpenChange={() => toggleGroup('email')}>
            <div className="border rounded-lg">
              <CollapsibleTrigger className="w-full">
                <div className="flex items-center justify-between p-3 hover:bg-muted/50 transition-colors">
                  <div className="flex items-center gap-3">
                    <Mail className="h-4 w-4 text-blue-500" />
                    <div className="text-left">
                      <Label className="font-medium cursor-pointer">Email Replies</Label>
                      <div className="text-xs text-muted-foreground">
                        {formatStat('email_positive_first')} positive, {formatStat('email_positive_second')} 2nd, {formatStat('email_followup')} follow-up, {formatStat('email_negative')} negative
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={setting.email_replies_enabled}
                      onCheckedChange={(value) => handleParentToggle('email_replies_enabled', value)}
                      disabled={updating === 'email_replies_enabled'}
                      onClick={(e) => e.stopPropagation()}
                    />
                    {expandedGroups.has('email') ? (
                      <ChevronDown className="h-4 w-4 text-muted-foreground" />
                    ) : (
                      <ChevronRight className="h-4 w-4 text-muted-foreground" />
                    )}
                  </div>
                </div>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="px-3 pb-3 space-y-2 border-t bg-muted/20">
                  <div className="flex items-center justify-between pt-2">
                    <Label className="text-sm">1st Positive Reply</Label>
                    <Switch
                      checked={setting.email_positive_first_enabled}
                      onCheckedChange={(value) => handleChildToggle('email_positive_first_enabled', value)}
                      disabled={updating === 'email_positive_first_enabled'}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label className="text-sm">2nd Positive Reply</Label>
                    <Switch
                      checked={setting.email_positive_second_enabled}
                      onCheckedChange={(value) => handleChildToggle('email_positive_second_enabled', value)}
                      disabled={updating === 'email_positive_second_enabled'}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label className="text-sm">Follow-up Reply</Label>
                    <Switch
                      checked={setting.email_followup_enabled}
                      onCheckedChange={(value) => handleChildToggle('email_followup_enabled', value)}
                      disabled={updating === 'email_followup_enabled'}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label className="text-sm">Negative/Neutral Reply</Label>
                    <Switch
                      checked={setting.email_negative_enabled}
                      onCheckedChange={(value) => handleChildToggle('email_negative_enabled', value)}
                      disabled={updating === 'email_negative_enabled'}
                    />
                  </div>
                </div>
              </CollapsibleContent>
            </div>
          </Collapsible>

          {/* LinkedIn Reply Notifications - Only show if client uses LinkedIn */}
          {setting.uses_linkedin && (
            <Collapsible open={expandedGroups.has('linkedin')} onOpenChange={() => toggleGroup('linkedin')}>
              <div className="border rounded-lg">
                <CollapsibleTrigger className="w-full">
                  <div className="flex items-center justify-between p-3 hover:bg-muted/50 transition-colors">
                    <div className="flex items-center gap-3">
                      <Linkedin className="h-4 w-4 text-blue-600" />
                      <div className="text-left">
                        <Label className="font-medium cursor-pointer">LinkedIn Replies</Label>
                        <div className="text-xs text-muted-foreground">
                          {formatStat('linkedin_positive_first')} positive, {formatStat('linkedin_positive_second')} 2nd, {formatStat('linkedin_followup')} follow-up, {formatStat('linkedin_negative')} negative, {formatStat('linkedin_neutral')} neutral
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={setting.linkedin_replies_enabled}
                        onCheckedChange={(value) => handleParentToggle('linkedin_replies_enabled', value)}
                        disabled={updating === 'linkedin_replies_enabled'}
                        onClick={(e) => e.stopPropagation()}
                      />
                      {expandedGroups.has('linkedin') ? (
                        <ChevronDown className="h-4 w-4 text-muted-foreground" />
                      ) : (
                        <ChevronRight className="h-4 w-4 text-muted-foreground" />
                      )}
                    </div>
                  </div>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <div className="px-3 pb-3 space-y-2 border-t bg-muted/20">
                    <div className="flex items-center justify-between pt-2">
                      <Label className="text-sm">1st Positive Reply</Label>
                      <Switch
                        checked={setting.linkedin_positive_first_enabled}
                        onCheckedChange={(value) => handleChildToggle('linkedin_positive_first_enabled', value)}
                        disabled={updating === 'linkedin_positive_first_enabled'}
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <Label className="text-sm">2nd Positive Reply</Label>
                      <Switch
                        checked={setting.linkedin_positive_second_enabled}
                        onCheckedChange={(value) => handleChildToggle('linkedin_positive_second_enabled', value)}
                        disabled={updating === 'linkedin_positive_second_enabled'}
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <Label className="text-sm">Follow-up Reply</Label>
                      <Switch
                        checked={setting.linkedin_followup_enabled}
                        onCheckedChange={(value) => handleChildToggle('linkedin_followup_enabled', value)}
                        disabled={updating === 'linkedin_followup_enabled'}
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <Label className="text-sm">Negative Reply</Label>
                      <Switch
                        checked={setting.linkedin_negative_enabled}
                        onCheckedChange={(value) => handleChildToggle('linkedin_negative_enabled', value)}
                        disabled={updating === 'linkedin_negative_enabled'}
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <Label className="text-sm">Neutral Reply</Label>
                      <Switch
                        checked={setting.linkedin_neutral_enabled}
                        onCheckedChange={(value) => handleChildToggle('linkedin_neutral_enabled', value)}
                        disabled={updating === 'linkedin_neutral_enabled'}
                      />
                    </div>
                  </div>
                </CollapsibleContent>
              </div>
            </Collapsible>
          )}

          {/* Performance Reports */}
          <Collapsible open={expandedGroups.has('performance')} onOpenChange={() => toggleGroup('performance')}>
            <div className="border rounded-lg">
              <CollapsibleTrigger className="w-full">
                <div className="flex items-center justify-between p-3 hover:bg-muted/50 transition-colors">
                  <div className="flex items-center gap-3">
                    <BarChart3 className="h-4 w-4 text-purple-500" />
                    <div className="text-left">
                      <Label className="font-medium cursor-pointer">Performance Reports</Label>
                      <div className="text-xs text-muted-foreground">
                        {formatStat('weekly_performance')} weekly, {formatStat('monthly_performance')} monthly
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={setting.performance_reports_enabled}
                      onCheckedChange={(value) => handleParentToggle('performance_reports_enabled', value)}
                      disabled={updating === 'performance_reports_enabled'}
                      onClick={(e) => e.stopPropagation()}
                    />
                    {expandedGroups.has('performance') ? (
                      <ChevronDown className="h-4 w-4 text-muted-foreground" />
                    ) : (
                      <ChevronRight className="h-4 w-4 text-muted-foreground" />
                    )}
                  </div>
                </div>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="px-3 pb-3 space-y-2 border-t bg-muted/20">
                  <div className="flex items-center justify-between pt-2">
                    <Label className="text-sm">Weekly Performance Summary</Label>
                    <Switch
                      checked={setting.weekly_performance_enabled}
                      onCheckedChange={(value) => handleChildToggle('weekly_performance_enabled', value)}
                      disabled={updating === 'weekly_performance_enabled'}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label className="text-sm">Monthly Performance Summary</Label>
                    <Switch
                      checked={setting.monthly_performance_enabled}
                      onCheckedChange={(value) => handleChildToggle('monthly_performance_enabled', value)}
                      disabled={updating === 'monthly_performance_enabled'}
                    />
                  </div>
                </div>
              </CollapsibleContent>
            </div>
          </Collapsible>

          {/* Outbound Reports */}
          <Collapsible open={expandedGroups.has('outbound')} onOpenChange={() => toggleGroup('outbound')}>
            <div className="border rounded-lg">
              <CollapsibleTrigger className="w-full">
                <div className="flex items-center justify-between p-3 hover:bg-muted/50 transition-colors">
                  <div className="flex items-center gap-3">
                    <TrendingUp className="h-4 w-4 text-indigo-500" />
                    <div className="text-left">
                      <Label className="font-medium cursor-pointer">Outbound Reports</Label>
                      <div className="text-xs text-muted-foreground">
                        {formatStat('daily_outbound')} daily, {formatStat('weekly_outbound')} weekly, {formatStat('monthly_outbound')} monthly
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={setting.outbound_reports_enabled}
                      onCheckedChange={(value) => handleParentToggle('outbound_reports_enabled', value)}
                      disabled={updating === 'outbound_reports_enabled'}
                      onClick={(e) => e.stopPropagation()}
                    />
                    {expandedGroups.has('outbound') ? (
                      <ChevronDown className="h-4 w-4 text-muted-foreground" />
                    ) : (
                      <ChevronRight className="h-4 w-4 text-muted-foreground" />
                    )}
                  </div>
                </div>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="px-3 pb-3 space-y-2 border-t bg-muted/20">
                  <div className="flex items-center justify-between pt-2">
                    <Label className="text-sm">Daily Outbound Report</Label>
                    <Switch
                      checked={setting.daily_outbound_enabled}
                      onCheckedChange={(value) => handleChildToggle('daily_outbound_enabled', value)}
                      disabled={updating === 'daily_outbound_enabled'}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label className="text-sm">Weekly Outbound Report</Label>
                    <Switch
                      checked={setting.weekly_outbound_enabled}
                      onCheckedChange={(value) => handleChildToggle('weekly_outbound_enabled', value)}
                      disabled={updating === 'weekly_outbound_enabled'}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label className="text-sm">Monthly Outbound Report</Label>
                    <Switch
                      checked={setting.monthly_outbound_enabled}
                      onCheckedChange={(value) => handleChildToggle('monthly_outbound_enabled', value)}
                      disabled={updating === 'monthly_outbound_enabled'}
                    />
                  </div>
                </div>
              </CollapsibleContent>
            </div>
          </Collapsible>

          {/* Other Reports */}
          <div className="flex items-center justify-between p-3 border rounded-lg">
            <div className="flex items-center gap-3">
              <Calendar className="h-4 w-4 text-green-500" />
              <div className="text-left">
                <Label className="font-medium cursor-pointer">Meeting Booked</Label>
                <div className="text-xs text-muted-foreground">
                  {formatStat('meeting_booked')} sent
                </div>
              </div>
            </div>
            <Switch
              checked={setting.meeting_booked_enabled}
              onCheckedChange={(value) => handleChildToggle('meeting_booked_enabled', value)}
              disabled={updating === 'meeting_booked_enabled'}
            />
          </div>

          {/* Legacy Reports (collapsed by default, marked as disabled) */}
          <Collapsible open={expandedGroups.has('legacy')} onOpenChange={() => toggleGroup('legacy')}>
            <div className="border rounded-lg border-dashed opacity-60">
              <CollapsibleTrigger className="w-full">
                <div className="flex items-center justify-between p-3 hover:bg-muted/50 transition-colors">
                  <div className="flex items-center gap-3">
                    <XCircle className="h-4 w-4 text-muted-foreground" />
                    <div className="text-left">
                      <Label className="font-medium cursor-pointer text-muted-foreground">Legacy Reports (Disabled)</Label>
                      <div className="text-xs text-muted-foreground">
                        Legacy/disabled notification types
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={setting.legacy_reports_enabled}
                      onCheckedChange={(value) => handleParentToggle('legacy_reports_enabled', value)}
                      disabled={updating === 'legacy_reports_enabled'}
                      onClick={(e) => e.stopPropagation()}
                    />
                    {expandedGroups.has('legacy') ? (
                      <ChevronDown className="h-4 w-4 text-muted-foreground" />
                    ) : (
                      <ChevronRight className="h-4 w-4 text-muted-foreground" />
                    )}
                  </div>
                </div>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="px-3 pb-3 space-y-2 border-t bg-muted/20">
                  <div className="flex items-center justify-between pt-2">
                    <Label className="text-sm text-muted-foreground">Daily Campaign Update (Legacy)</Label>
                    <Switch
                      checked={setting.daily_campaign_update_enabled}
                      onCheckedChange={(value) => handleChildToggle('daily_campaign_update_enabled', value)}
                      disabled={updating === 'daily_campaign_update_enabled'}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label className="text-sm text-muted-foreground">Daily Send Summary (Legacy)</Label>
                    <Switch
                      checked={setting.daily_send_summary_enabled}
                      onCheckedChange={(value) => handleChildToggle('daily_send_summary_enabled', value)}
                      disabled={updating === 'daily_send_summary_enabled'}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label className="text-sm text-muted-foreground">Daily Positive Summary (Legacy)</Label>
                    <Switch
                      checked={setting.daily_positive_summary_enabled}
                      onCheckedChange={(value) => handleChildToggle('daily_positive_summary_enabled', value)}
                      disabled={updating === 'daily_positive_summary_enabled'}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label className="text-sm text-muted-foreground">Monthly ROI Report (Legacy)</Label>
                    <Switch
                      checked={setting.monthly_roi_enabled}
                      onCheckedChange={(value) => handleChildToggle('monthly_roi_enabled', value)}
                      disabled={updating === 'monthly_roi_enabled'}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label className="text-sm text-muted-foreground">Weekly Objection Analysis (Legacy)</Label>
                    <Switch
                      checked={setting.weekly_objection_analysis_enabled}
                      onCheckedChange={(value) => handleChildToggle('weekly_objection_analysis_enabled', value)}
                      disabled={updating === 'weekly_objection_analysis_enabled'}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label className="text-sm text-muted-foreground">Hot Lead Alert (Legacy)</Label>
                    <Switch
                      checked={setting.hot_lead_alert_enabled}
                      onCheckedChange={(value) => handleChildToggle('hot_lead_alert_enabled', value)}
                      disabled={updating === 'hot_lead_alert_enabled'}
                    />
                  </div>
                </div>
              </CollapsibleContent>
            </div>
          </Collapsible>
        </div>

        {/* Overall Stats */}
        {setting.notifications_sent > 0 && (
          <div className="pt-3 border-t">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">
                📊 Total sent: <span className="font-medium">{setting.notifications_sent}</span>
              </span>
              {setting.last_notification_at && (
                <span className="text-muted-foreground">
                  Last: {formatDistanceToNow(new Date(setting.last_notification_at), { addSuffix: true })}
                </span>
              )}
            </div>
          </div>
        )}

        {/* Metadata */}
        {setting.enabled_at && (
          <div className="pt-2 text-xs text-muted-foreground">
            Last updated: {formatDistanceToNow(new Date(setting.updated_at), { addSuffix: true })}
            {setting.enabled_by && ` by ${setting.enabled_by}`}
          </div>
        )}
      </CardContent>
    </Card>
  )
}
